import { camelCase } from 'lodash-es';
import { formatLabeledNumber } from '@/lib/goal/progress';
import { goalChangeField } from 'shared/constants.json';
import { textByLang } from 'shared/lib/language';

export const numberFields = [goalChangeField.start, goalChangeField.end, goalChangeField.threshold];

const fieldLabelKeys = {
  [goalChangeField.title]: 'goalChanges.fields.title',
  [goalChangeField.icon]: 'goalChanges.fields.icon',
  [goalChangeField.publishedAt]: 'goalChanges.fields.publishedAt',
  [goalChangeField.description]: 'goalChanges.fields.description',
  [goalChangeField.progressMeasurement]: 'progressMeasurement.measurement',
  [goalChangeField.aggregationMethod]: 'progressMeasurement.calculateBy',
  [goalChangeField.start]: 'metricForm.start',
  [goalChangeField.end]: 'metricForm.end',
  [goalChangeField.threshold]: 'metricForm.threshold',
  [goalChangeField.thresholdTargetArea]: 'goalChanges.fields.thresholdTargetArea',
  [goalChangeField.metric]: 'metricForm.metric',
  [goalChangeField.alignedItem]: 'metricForm.alignedItems',
  [goalChangeField.goalCycle]: 'goal.goalEditor.goalCycle',
  [goalChangeField.unboundedProgress]: 'alignedItemAdvancedOptions.settings.unboundedProgress',
  [goalChangeField.disableStatusAutoUpdate]: 'alignedItemAdvancedOptions.settings.disableStatusAutoUpdate',
};

export const propertyLabel = (property, language) => (property?.label === undefined ? '' : textByLang(property.label, language));

export const changeFieldLabel = (change, { t, language, goalName }) => {
  if (change.field === goalChangeField.property) {
    return propertyLabel(change.property, language);
  }
  if (change.field === goalChangeField.parent) {
    return t('goal.goalEditor.parent', { title: goalName });
  }
  return t(fieldLabelKeys[change.field]);
};

export const isEmptyScalarValue = (value) => value === null || value === undefined || value === '';

export const formatScalarValue = (field, value, { t, metric, language }) => {
  if (field === goalChangeField.publishedAt) {
    return t(isEmptyScalarValue(value) ? 'goalChanges.draft' : 'goalChanges.published');
  }
  if (isEmptyScalarValue(value)) {
    return t('general.empty');
  }
  if (numberFields.includes(field)) {
    return formatLabeledNumber(parseFloat(value), metric, language);
  }
  switch (field) {
    case goalChangeField.progressMeasurement:
      return t(`metricForm.${camelCase(value)}`);
    case goalChangeField.aggregationMethod:
      return t(`metricForm.aggregationMethod.${value}`);
    case goalChangeField.thresholdTargetArea:
      return t(`goalThresholdTargetArea.${value}`);
    case goalChangeField.unboundedProgress:
      return t(value === 'true' ? 'goalChanges.on' : 'goalChanges.off');
    // Labelled as enabling the automatic status update, so the stored flag is shown inverted.
    case goalChangeField.disableStatusAutoUpdate:
      return t(value === 'true' ? 'goalChanges.off' : 'goalChanges.on');
    case goalChangeField.alignedItem:
      return new Intl.NumberFormat(language).format(parseFloat(value));
    default:
      return value;
  }
};

// An item added with the default weight needs no mention; one added with another weight does.
export const shownWeights = (relation) => {
  if (relation.field !== goalChangeField.alignedItem || relation.value === null) {
    return [];
  }
  if (relation.previousValue === null) {
    return relation.value === '1' ? [] : [relation.value];
  }
  return [relation.previousValue, relation.value];
};

export const joinFieldLabels = (labels, language) => new Intl.ListFormat(language, { style: 'long', type: 'conjunction' }).format(labels);
